import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Tag, Plus, Trash2, Edit3, ToggleLeft, ToggleRight, X, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '../../lib/api';
import ImageUpload from '../../components/ImageUpload';

const emptyForm = { title: '', description: '', image: '', link_url: '', link_text: 'Learn More', badge_text: 'Special Offer', is_active: true, is_main: false, is_global: true, location_id: '', start_date: '', end_date: '' };
const inputCls = 'bg-[#232323] border-white/[0.10] rounded-lg h-9 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1 block';

export default function AdminOffers() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => { if (!authLoading && !isAdmin) navigate('/login'); }, [isAdmin, authLoading, navigate]);

  const fetchOffers = () => {
    setLoading(true);
    API.get('/admin/offers').then(r => { setOffers(r.data.offers || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchOffers();
    API.get('/admin/locations').then(r => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setShowForm(true); };
  const openEdit = (offer) => { setEditing(offer.id); setForm({ ...emptyForm, ...offer }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, location_id: form.location_id || '' };
      if (editing) { await API.put(`/admin/offers/${editing}`, payload); toast.success('Offer updated'); }
      else { await API.post('/admin/offers', payload); toast.success('Offer created'); }
      fetchOffers();
      closeForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (id, current) => {
    try { await API.put(`/admin/offers/${id}`, { is_active: !current }); fetchOffers(); } catch { toast.error('Failed'); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try { await API.delete(`/admin/offers/${id}`); fetchOffers(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-offers">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p><h1 className="font-heading text-2xl text-white font-bold mt-1">Offers & Promotions</h1></div>
          <Button onClick={openCreate} className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="create-offer-btn"><Plus className="w-3.5 h-3.5" /> New Offer</Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <AdminNav active="/admin/offers" />

        {showForm && (
          <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit}
            className="bg-[#1c1c1c] border border-gold/20 p-6 mb-8 space-y-4 rounded-xl" data-testid="offer-form">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-base text-white font-semibold">{editing ? 'Edit Offer' : 'Create Offer'}</h3>
              <button type="button" onClick={closeForm} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Title</label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} data-testid="offer-title" /></div>
              <div><label className={labelCls}>Badge Text</label><Input value={form.badge_text} onChange={e => setForm({ ...form, badge_text: e.target.value })} className={inputCls} data-testid="offer-badge" /></div>
              <div><label className={labelCls}>Link URL</label><Input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} placeholder="https://..." className={inputCls} data-testid="offer-link-url" /></div>
              <div><label className={labelCls}>Link Text</label><Input value={form.link_text} onChange={e => setForm({ ...form, link_text: e.target.value })} className={inputCls} data-testid="offer-link-text" /></div>
              <div>
                <label className={labelCls}>Location (optional)</label>
                <select value={form.location_id} onChange={e => setForm({ ...form, location_id: e.target.value })}
                  className="w-full bg-[#232323] border border-white/[0.10] rounded-lg h-9 text-sm px-3 text-white focus:outline-none focus:border-gold" data-testid="offer-location">
                  <option value="">All Locations (Global)</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-4 pb-1">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_main} onChange={e => setForm({ ...form, is_main: e.target.checked })} className="rounded border-white/[0.15] bg-[#232323]" /><span className="text-xs text-white/50">Main Landing Offer</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-white/[0.15] bg-[#232323]" /><span className="text-xs text-white/50">Active</span></label>
              </div>
              <div><label className={labelCls}>Start Date</label><Input type="datetime-local" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="offer-start" /></div>
              <div><label className={labelCls}>End Date</label><Input type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="offer-end" /></div>
            </div>
            <div><label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                className="w-full bg-[#232323] border border-white/[0.10] rounded-lg p-3 text-sm text-white resize-none focus:outline-none focus:border-gold placeholder:text-white/25" data-testid="offer-description" />
            </div>
            <ImageUpload value={form.image} onChange={url => setForm({ ...form, image: url })} label="Offer Image (optional)" />
            <div className="flex gap-3">
              <Button type="submit" className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest" data-testid="offer-submit-btn">{editing ? 'Update Offer' : 'Create Offer'}</Button>
              <Button type="button" variant="ghost" onClick={closeForm} className="text-white/30 text-xs">Cancel</Button>
            </div>
          </motion.form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
        ) : offers.length === 0 ? (
          <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-12 text-center">
            <Tag className="w-10 h-10 text-white/15 mx-auto mb-4" />
            <h2 className="font-heading text-lg text-white font-semibold mb-2">No Offers Yet</h2>
            <p className="text-sm text-white/35">Create your first promotional offer to attract guests.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {offers.map((o, i) => (
              <motion.div key={o.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                className="flex items-center gap-4 p-4 bg-[#1c1c1c] border border-white/[0.06] rounded-xl" data-testid={`admin-offer-${o.id}`}>
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-gold" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <h3 className="text-white text-sm font-medium truncate">{o.title}</h3>
                    <Badge className={`text-[10px] ${o.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-white/[0.04] text-white/30 border-white/[0.08]'}`}>{o.is_active ? 'Active' : 'Inactive'}</Badge>
                    {o.is_main && <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[10px]">Main</Badge>}
                  </div>
                  <p className="text-xs text-white/35 truncate">{o.location_name || 'All Locations'}{o.description ? ` — ${o.description}` : ''}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}><Edit3 className="w-4 h-4 text-white/30" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(o.id, o.is_active)} data-testid={`toggle-offer-${o.id}`}>{o.is_active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5 text-white/30" />}</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteOffer(o.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-offer-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
